import { useEffect, useState } from 'react';
import api from './api';
import { ROLES, roleLabel, roleBadgeStyle } from './roles';
import Swal from 'sweetalert2';

export default function Dashboard({ onConnect, onLogout, onAdmin, onReseller }) {
  const [vms, setVms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const name = sessionStorage.getItem('auth_name') || 'User';
  const role = parseInt(sessionStorage.getItem('auth_role') || '0', 10);
  const isAdmin = role === ROLES.ADMIN;
  const isReseller = role === ROLES.RESELLER;

  useEffect(() => {
    api.get('/Member/List')
      .then(res => setVms(res.data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#1a1a1a', color: '#e5e7eb', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '12px 24px',
        background: '#2d2d2d', borderBottom: '1px solid #444',
      }}>
        <span style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>Console Management</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: '#9ca3af', marginRight: 8 }}>{name}</span>
        <span style={{ ...roleBadgeStyle(role), marginRight: 16 }}>{roleLabel(role)}</span>
        {isReseller && (
          <button onClick={onReseller} style={{
            padding: '4px 12px', borderRadius: 6, border: 'none',
            background: '#374151', color: '#d1d5db', fontSize: 12,
            cursor: 'pointer', marginRight: 8,
          }}>
            Reseller
          </button>
        )}
        {isAdmin && (
          <button onClick={onAdmin} style={{
            padding: '4px 12px', borderRadius: 6, border: 'none',
            background: '#374151', color: '#d1d5db', fontSize: 12,
            cursor: 'pointer', marginRight: 8,
          }}>
            Admin
          </button>
        )}
        <button onClick={async () => {
          const { value } = await Swal.fire({
            title: 'Change Password',
            html: `
              <input id="swal-cur" type="password" placeholder="Current password" class="swal2-input" style="margin-bottom:8px">
              <input id="swal-new" type="password" placeholder="New password" class="swal2-input">
            `,
            showCancelButton: true,
            confirmButtonText: 'Change',
            confirmButtonColor: '#1e3a5f',
            cancelButtonColor: '#374151',
            background: '#1f2937',
            color: '#f9fafb',
            preConfirm: () => {
              const cur = document.getElementById('swal-cur').value;
              const nw = document.getElementById('swal-new').value;
              if (!cur || !nw) { Swal.showValidationMessage('Both fields are required'); return false; }
              return { cur, nw };
            },
          });
          if (!value) return;
          try {
            const fd = new FormData();
            fd.append('Password', value.cur);
            fd.append('NewPassword', value.nw);
            const res = await api.post('/Member/ChangePassword', fd);
            if (!res.data.status) { Swal.fire({ title: 'Failed', icon: 'error', background: '#1f2937', color: '#f9fafb' }); return; }
            Swal.fire({ title: 'Password Changed', icon: 'success', timer: 1500, showConfirmButton: false, background: '#1f2937', color: '#f9fafb' });
          } catch (err) {
            Swal.fire({ title: 'Error', text: err.response?.data?.message ?? err.message, icon: 'error', background: '#1f2937', color: '#f9fafb' });
          }
        }} style={{
          padding: '4px 12px', borderRadius: 6, border: 'none',
          background: '#374151', color: '#d1d5db', fontSize: 12,
          cursor: 'pointer', marginRight: 8,
        }}>
          Change Password
        </button>
        <button onClick={async () => { try { await api.get('/Authentication/Logout'); } finally { onLogout(); } }} style={{
          padding: '4px 12px', borderRadius: 6, border: 'none',
          background: '#374151', color: '#d1d5db', fontSize: 12,
          cursor: 'pointer',
        }}>
          Logout
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '32px 24px', maxWidth: 900, margin: '0 auto' }}>
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600, color: '#f9fafb' }}>
          Virtual Machines
        </h2>

        {loading && <div style={{ color: '#9ca3af' }}>Loading...</div>}
        {error && <div style={{ color: '#fca5a5' }}>Error: {error}</div>}

        {!loading && !error && vms.length === 0 && (
          <div style={{ color: '#6b7280' }}>No VMs found.</div>
        )}

        {!loading && !error && vms.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {vms.map(vm => (
              <div key={vm.vmid} style={{
                background: '#2d2d2d', border: '1px solid #3f3f46',
                borderRadius: 10, padding: '18px 20px',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: '#f3f4f6' }}>
                  {vm.name || 'Unnamed VM'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                    background: vm.state === 1 ? '#14532d' : '#374151',
                    color: vm.state === 1 ? '#bbf7d0' : '#9ca3af',
                  }}>
                    {vm.state === 1 ? 'Running' : 'Stopped'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  IP: <span style={{ color: '#9ca3af' }}>{vm.ip || '—'}</span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  MAC: <span style={{ color: '#9ca3af' }}>{vm.mac_address || '—'}</span>
                </div>
                <button
                  onClick={() => onConnect(vm)}
                  style={{
                    marginTop: 4, padding: '7px 0', borderRadius: 6, border: 'none',
                    background: '#1e3a5f', color: '#93c5fd',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Console
                </button>
                <button
                  onClick={async () => {
                    const { isConfirmed } = await Swal.fire({
                      title: 'Reboot VM?', text: `Reboot "${vm.name || vm.vmid}"?`, icon: 'warning',
                      showCancelButton: true, confirmButtonColor: '#92400e', cancelButtonColor: '#374151',
                      confirmButtonText: 'Reboot', background: '#1f2937', color: '#f9fafb',
                    });
                    if (!isConfirmed) return;
                    try {
                      await api.put(`/Member/Reboot/${vm.vmid}`);
                      Swal.fire({ title: 'Reboot initiated', icon: 'success', timer: 1500, showConfirmButton: false, background: '#1f2937', color: '#f9fafb' });
                    } catch (err) {
                      Swal.fire({ title: 'Error', text: err.response?.data?.message ?? err.message, icon: 'error', background: '#1f2937', color: '#f9fafb' });
                    }
                  }}
                  style={{
                    padding: '7px 0', borderRadius: 6, border: 'none',
                    background: '#451a03', color: '#fb923c',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Reboot
                </button>
              </div>
            ))}
          </div>
        )}


      </div>
    </div>
  );
}
